/**
 * Help for the server-rendered pages (login, second factor, enrollment, error pages). Those
 * pages stay free of a framework: this only adds the button and the F1 handler, and loads the
 * React panel on the first request. The member app's entry (./standalone.tsx) loads its panel
 * with the member articles, the operator portal's (assets/ops/help/standalone.tsx) its own.
 */
export interface StandaloneHelpPanel {
  mountStandaloneHelp: (container: HTMLElement, trigger: HTMLButtonElement, returnFocus: HTMLElement | null) => () => void;
}

export function initializeStandaloneHelp(container: HTMLElement, load: () => Promise<StandaloneHelpPanel>) {
  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.textContent = "Help";
  trigger.setAttribute("aria-label", "Page help (F1)");
  trigger.setAttribute("aria-keyshortcuts", "F1");
  trigger.title = "Page help (F1)";
  trigger.className = "fixed right-3 top-3 z-30 h-7 cursor-default select-none rounded-md border border-line bg-surface px-2.5 text-xs font-medium text-muted outline-none hover:bg-subtle hover:text-fg focus-visible:ring-2 focus-visible:ring-focus";
  // Announces the load of the panel; `empty:hidden` keeps it invisible while silent.
  const status = document.createElement("p");
  status.setAttribute("role", "status");
  status.className = "fixed right-3 top-12 z-30 max-w-xs rounded-md border border-line bg-surface px-2.5 py-1 text-sm text-fg shadow-card empty:hidden";
  const panel = document.createElement("div");
  container.append(trigger, status, panel);

  let loading = false;
  let disposed = false;
  let unmount: (() => void) | undefined;
  const open = async () => {
    if (loading || disposed) {
      return;
    }
    loading = true;
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    trigger.setAttribute("aria-busy", "true");
    status.textContent = "Loading help…";
    try {
      const { mountStandaloneHelp } = await load();
      if (disposed) {
        return;
      }
      unmount = mountStandaloneHelp(panel, trigger, returnFocus);
      // The mounted provider now owns both F1 and the persistent trigger.
      detach();
      status.textContent = "";
    } catch {
      if (!disposed) {
        status.textContent = "Help could not load. Press F1 or Help to retry.";
      }
    } finally {
      loading = false;
      trigger.removeAttribute("aria-busy");
    }
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || "f1" !== event.key.toLowerCase()) {
      return;
    }
    // F1 is help here too, not the browser's help.
    event.preventDefault();
    void open();
  };
  const detach = () => {
    trigger.removeEventListener("click", open);
    window.removeEventListener("keydown", onKeyDown);
  };
  trigger.addEventListener("click", open);
  window.addEventListener("keydown", onKeyDown);

  return () => {
    disposed = true;
    detach();
    unmount?.();
    container.replaceChildren();
  };
}
